import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ShieldCheck, Search, Eye, CheckCircle, XCircle } from 'lucide-react';

interface KYCSeller {
  id: string;
  full_name: string;
  business_name: string;
  kyc_status: string;
  kyc_submitted_at: string;
  kyc_id_image_url: string | null;
  kyc_selfie_url: string | null;
  kyc_nin_encrypted: string | null;
}

export default function KYCReview() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState<KYCSeller[]>([]);
  const [search, setSearch] = useState('');
  const [selectedSeller, setSelectedSeller] = useState<KYCSeller | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    fetchPendingKYC();
  }, []);

  const fetchPendingKYC = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, business_name, kyc_status, kyc_submitted_at, kyc_id_image_url, kyc_selfie_url, kyc_nin_encrypted')
        .in('kyc_status', ['pending', 'rejected', 'approved'])
        .order('kyc_submitted_at', { ascending: false });

      if (error) throw error;
      setSellers((data as KYCSeller[]) || []);
    } catch (error) {
      console.error('Error fetching KYC:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (sellerId: string, approved: boolean) => {
    setProcessing(true);
    try {
      const { error } = await supabase.rpc('review_kyc', {
        p_seller_id: sellerId,
        p_approved: approved,
        p_reason: approved ? null : rejectionReason,
      });

      if (error) throw error;

      // Send SMS notification for KYC result
      try {
        const { data: sellerProfile } = await supabase
          .from('profiles')
          .select('phone')
          .eq('id', sellerId)
          .single();

        if (sellerProfile?.phone) {
          const smsMessage = approved
            ? 'Congratulations! Your KYC verification has been approved. You can now withdraw funds from your escrow account.'
            : `Your KYC verification was not approved. Reason: ${rejectionReason || 'Please resubmit with valid documents.'}`;
          
          await supabase.functions.invoke('send-sms-notification', {
            body: { phone: sellerProfile.phone, message: smsMessage }
          });
        }
      } catch (smsErr) {
        console.error('KYC SMS notification error (non-blocking):', smsErr);
      }

      toast({
        title: approved ? 'KYC Approved' : 'KYC Rejected',
        description: `Seller has been ${approved ? 'verified' : 'notified of rejection'}.`,
      });

      setSelectedSeller(null);
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchPendingKYC();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const filtered = sellers.filter(s =>
    (s.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.business_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = sellers.filter(s => s.kyc_status === 'pending').length;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                KYC Verification
                {pendingCount > 0 && (
                  <Badge variant="destructive">{pendingCount} pending</Badge>
                )}
              </CardTitle>
              <CardDescription>Review seller identity verification requests</CardDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sellers..."
                className="pl-10 w-64"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No KYC submissions found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>NIN</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCell className="font-medium">{seller.full_name || 'N/A'}</TableCell>
                    <TableCell>{seller.business_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant={
                        seller.kyc_status === 'approved' ? 'default' :
                        seller.kyc_status === 'pending' ? 'secondary' : 'destructive'
                      }>
                        {seller.kyc_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {seller.kyc_submitted_at
                        ? new Date(seller.kyc_submitted_at).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {seller.kyc_nin_encrypted ? (
                        <span className="text-xs font-mono">****{seller.kyc_nin_encrypted.slice(-4)}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not provided</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {seller.kyc_id_image_url && (
                          <a href={seller.kyc_id_image_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm"><Eye className="h-3 w-3 mr-1" /> ID</Button>
                          </a>
                        )}
                        {seller.kyc_selfie_url && (
                          <a href={seller.kyc_selfie_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm"><Eye className="h-3 w-3 mr-1" /> Selfie</Button>
                          </a>
                        )}
                        {!seller.kyc_id_image_url && !seller.kyc_selfie_url && (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {seller.kyc_status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="default"
                            disabled={processing}
                            onClick={() => handleReview(seller.id, true)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={processing}
                            onClick={() => { setSelectedSeller(seller); setShowRejectDialog(true); }}
                          >
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject KYC</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting {selectedSeller?.full_name || selectedSeller?.business_name}'s KYC.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={processing || !rejectionReason.trim()}
              onClick={() => selectedSeller && handleReview(selectedSeller.id, false)}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
